import type { ReactNode } from 'react';
import type { ApiUserAccount } from 'src/types/userAuth';

import { useQueryClient } from '@tanstack/react-query';
import { useRef, useMemo, useEffect, useContext, createContext } from 'react';

import { isTelegramWebApp } from 'src/utils/telegram';
import { dropLegacyTokens } from 'src/utils/storefrontSession';
import { localStore, markSignedIn, dropSignedInHint } from 'src/utils/userSession';

import { removeReadsOfOtherSessions } from 'src/queries';

import { useHydrated } from './useHydrated';
import { useUserSession } from './useUserSession';

/**
 * Who is in front of the storefront, for every screen.
 *
 * One session — the account's, signed in by e-mail and code (api#402, step 2), held as the api's
 * cookie together with the dealer cabinet since the single sign-in (api#454). The server knows
 * nothing of it: it renders every page for an anonymous visitor, and so does the render in which
 * the browser takes the server's markup over. The visitor's own session is asked for right after
 * it (`/user/auth/me`). The wallet is not a session: it is linked to the account (Profile → Linked
 * wallets) and connected at the moment of paying or signing.
 */
export interface Session {
  /** The account behind the session (`/user/auth/me`), once the api has answered. */
  account?: ApiUserAccount;
  /** A session is held on this device. */
  isLoggedIn: boolean;
  /**
   * The account holds a linked wallet (its `primaryWalletId` — the first wallet linked becomes
   * the primary for good). `undefined` while the account is not known yet. What the Buy / Book
   * gate reads (`purchaseGate.ts`).
   */
  hasLinkedWallet?: boolean;
  /** The session is still being checked — what the shell holds the splash for. */
  isLoading: boolean;
  /**
   * The api has said whether this browser holds a session — an account or a refusal, never a
   * failure — for every visitor (`isLoading` waits only for a browser that signed in here before).
   * What the assistant keys its conversation's owner on (app#397).
   */
  isChecked: boolean;
  /**
   * A session is held on this device and has not been confirmed yet. A server-rendered page is on
   * screen through that second (a screen behind the splash is not), so anything that depends on
   * who the visitor is — the Buy button first of all — waits instead of showing the anonymous
   * answer to a signed-in person.
   */
  isPending: boolean;
  /** Inside Telegram: the mini-app or its in-app browser, where a wallet adapter cannot open. */
  isTMA: boolean;
}

const SessionContext = createContext<Session | null>(null);

export const SessionProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const { account, isLoading, isLoggedIn, isRejected, isChecked, epoch } = useUserSession();
  const hydrated = useHydrated();
  // Telegram's global is the browser's knowledge: read only once the browser has the page.
  const isTMA = hydrated && isTelegramWebApp();
  const isPending = hydrated && isLoading;

  // The tokens a browser kept from before — the wallet session's (api#402, step 2) and the account's
  // two (api#454) — are dropped on start: the session is the api's cookie, and nothing here can use
  // or end a token (see `dropLegacyTokens`).
  useEffect(() => {
    const store = localStore();

    if (store) dropLegacyTokens(store);
  }, []);

  // The account behind the cookie changed under this tab with no sign-out seen here: signed out and
  // in as someone else in the dealer cabinet while this tab was in the background, learnt by the
  // heartbeat. A new epoch, as a sign-in here would be — the reads of the account before are keyed
  // out of the cache (below) and never served to the one after.
  const lastAccountId = useRef<string | undefined>(undefined);
  const accountId = account?.id;

  useEffect(() => {
    if (accountId === undefined) return;

    const before = lastAccountId.current;
    lastAccountId.current = accountId;
    if (before !== undefined && before !== accountId) markSignedIn();
  }, [accountId]);

  // The api refused the session (401/403): the hint goes, so the next visit starts as a guest's and
  // the other tabs learn of it. Once, here — not in every screen that reads the session.
  useEffect(() => {
    if (isRejected) dropSignedInHint();
  }, [isRejected]);

  // A new epoch — signed in or out here, or in another tab (the `storage` event reaches the store
  // too). What was read under the one before goes: the account, its primary wallet, the deals and
  // the notifications (app#248) — keyed by the old session they can no longer be served to anyone,
  // and they have no business staying in memory either.
  useEffect(() => {
    removeReadsOfOtherSessions(queryClient);
  }, [epoch, queryClient]);

  const session = useMemo(
    () => ({
      account,
      isLoggedIn,
      hasLinkedWallet: account ? !!account.primaryWalletId : undefined,
      isLoading,
      isChecked,
      isPending,
      isTMA,
    }),
    [account, isLoggedIn, isLoading, isChecked, isPending, isTMA]
  );

  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
};

export const useSession = (): Session => {
  const session = useContext(SessionContext);

  if (!session) throw new Error('useSession is read outside SessionProvider');

  return session;
};
